import {
  Component,
  computed,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  WritableSignal,
  signal,
} from '@angular/core';
import { CurrencyPipe, DatePipe, DecimalPipe, NgIf } from '@angular/common';
import {
  AnalyticsInfo,
  AnalyticsRangeContext,
  ChartType,
  ComparePeriod,
  FormatterType,
  getComparePeriodLabel,
} from '@services/analytics-services/core';
import * as Highcharts from 'highcharts';
import { BadgesComponent } from '@enginuity/core/molecules/badges/badges.component';
import { FormattingService } from '@services/analytics-services/core/formatting.service';
import { AnalyticsGraphService } from '@services/analytics-services/core/analytics-graph.service';
import { HighchartsChartModule } from 'highcharts-angular';
import { formatHour } from '@services/core-services/utils/date.utils';
import { BoxTabComponent } from '@enginuity/core/molecules/box-tab/box-tab.component';

// Interface extending Highcharts Options to include our custom properties
interface AnalyticsChartOptions extends Highcharts.Options {
  context?: AnalyticsRangeContext;
  compareTimeOffset?: number;
  comparePeriodLabel?: string;
  comparePeriod?: ComparePeriod;
  component?: {
    name: string;
    unit: string;
    formatter: FormatterType;
  };
}

@Component({
  selector: 'app-analytics-widget',
  imports: [NgIf, BadgesComponent, HighchartsChartModule, BoxTabComponent],
  templateUrl: './analytics-widget.component.html',
  styleUrl: './analytics-widget.component.scss',
  styles: [
    `
      :host {
        display: block;
        width: 100%;
        height: 100%;
      }
    `,
  ],
  providers: [DatePipe, DecimalPipe, CurrencyPipe, FormattingService, AnalyticsGraphService],
})
export class AnalyticsWidgetComponent implements OnChanges {
  // Make formatHour accessible for the chart options
  private static formatHour = formatHour;

  private static readonly CHART_COLORS = {
    LINE_COLORS: [
      'var(--active-default)',
      'var(--color-green)',
      'var(--color-orange)',
      'var(--alert-160)',
    ],
    BAR_COLORS: ['var(--color-green)', 'var(--alert-160)'],
    DEFAULT_COLOR: 'var(--color-green)',
  } as const;

  private static readonly METRICS_MAPPING = {
    DELIVERED: 'delivered',
    FAILED: 'failed',
  } as const;

  private static readonly SERIES_DISPLAY_NAMES = {
    average_latency: 'Average',
    p50_latency: 'P50',
    p75_latency: 'P75',
    p95_latency: 'P95',
    Delivered: 'Delivered',
    Failed: 'Failed',
  } as const;

  Highcharts: typeof Highcharts = Highcharts;
  @Input() metricsKey: string = 'viewed';
  @Input() title: string = '';
  @Input() height: number | 'auto' = 'auto';
  @Input() subtitle: string = '';
  @Input() analytics: AnalyticsInfo<any> | undefined | null = undefined;
  @Input() type: ChartType = 'line';
  @Input() formatter: FormatterType | undefined = 'number';
  @Input() compare: ComparePeriod = ComparePeriod.Custom;
  @Input() context!: AnalyticsRangeContext;
  @Input() hideStats: boolean = false;
  @Input() stackedSeries: string[] = [];
  @Input() chartMode: 'single' | 'multiple-line' | 'stacked-bar' = 'single';

  // State for view mode (Regular vs Cumulative)
  protected displayMode: WritableSignal<'regular' | 'cumulative'> = signal('regular');

  // Tab definitions for the view mode toggle
  protected viewModeTabs = [
    {
      id: 'regular',
      tooltip: 'Regular View: See values for each interval',
      active: true,
      icon: 'line_chart',
    },
    {
      id: 'cumulative',
      tooltip: 'Cumulative View: Track the total sum over time',
      active: false,
      icon: 'cumulative_chart',
    },
  ];

  // Computed signal to update active state in tabs based on displayMode
  protected activeViewModeTabs = computed(() => {
    const currentMode = this.displayMode();
    return this.viewModeTabs.map(tab => ({
      ...tab,
      active: tab.id === currentMode,
    }));
  });

  // Used to force chart redraw when context changes
  protected chartUpdateFlag: number = 0;

  // Convert chartOptions to a WritableSignal
  protected chartOptions: WritableSignal<any> = signal({
    chart: {
      type: 'spline',
      animation: {
        duration: 500,
      },
    },
    time: {
      timezone: 'local',
      useUTC: false,
    },
    title: null,
    subtitle: null,

    yAxis: {
      title: {
        text: null,
      },
      gridLineColor: 'var(--primary-30)',
      gridLineWidth: 0.5,
      gridLineDashStyle: 'Dash',
    },
    xAxis: {
      type: 'datetime',
      labels: {
        formatter: function (this: Highcharts.AxisLabelsFormatterContextObject): string {
          // Cast to our custom options type to access context
          const chartOptions = this.axis.chart.options as AnalyticsChartOptions;
          const chartContext = chartOptions.context;
          const interval = chartContext?.interval;

          if (interval === 'hour') {
            const date = new Date(this.value);
            // Use the static formatHour method referenced from the component
            return (AnalyticsWidgetComponent as any).formatHour(date.getHours());
          } else if (interval === 'month') {
            const date = new Date(this.value);
            const monthShort = date.toLocaleString('en-US', { month: 'short' });
            const yearShort = date.getFullYear().toString().slice(-2); // Get last two digits of year
            return `${monthShort} ${yearShort}`;
          }
          // Use default formatter for other intervals
          return this.axis.defaultLabelFormatter.call(this);
        },
      },
      lineColor: 'var(--primary-30)',
    },
    legend: {
      enabled: false,
      layout: 'vertical',
      align: 'right',
      verticalAlign: 'middle',
    },

    plotOptions: {
      series: {
        animation: {
          duration: 500,
        },
        label: {
          connectorAllowed: false,
        },
      },
      column: {
        stacking: undefined,
        dataLabels: {
          enabled: false,
        },
        borderWidth: 0,
        pointPadding: 0.1,
        groupPadding: 0.1,
      },
    },
    tooltip: {
      enabled: true,
      useHTML: true,
      shared: true,
      crosshairs: true,
      backgroundColor: 'rgba(35, 35, 35, 0.95)',
      borderWidth: 0,
      borderRadius: 8,
      shadow: true,
      style: {
        color: '#ffffff',
        fontSize: '12px',
        fontWeight: 'normal',
      },
      formatter: function (this: any): string {
        try {
          const chartOptions = this.series.chart.options as AnalyticsChartOptions;
          const comparePeriodLabel = chartOptions.comparePeriodLabel || '';
          const component = (chartOptions.component ?? {}) as {
            name?: string;
            unit?: string;
            formatter?: FormatterType;
            isStacked?: boolean;
            isMultipleLineSeries?: boolean;
          };
          const context = (chartOptions.context ?? {}) as Partial<AnalyticsRangeContext>;
          const chartTitle = component.name || this.series.name || 'Data';
          const points = this.points || [this.point];

          // Check if this is a stacked chart or multiple line series
          const isStackedChart = component.isStacked || false;
          const isMultipleLineSeries = component.isMultipleLineSeries || false;

          if (isStackedChart || isMultipleLineSeries) {
            // Handle stacked chart or multiple line series tooltip
            const timestamp = points[0]?.x;
            const date = new Date(timestamp);
            const dayOfMonth = date.getDate();
            const monthShort = date.toLocaleString('en-US', { month: 'short' });
            const year = date.getFullYear();
            const dateLabel = `${dayOfMonth} ${monthShort} ${year}`;

            let tooltipHtml = `
            <div class="custom-chart-tooltip" style="padding:12px;">
              <div class="d-flex flex-column spacing-8 align-items-start w-100">
                <div style="width: 100%;">
                  <h4 class="h4-bold primary-0" style="margin: 0">${chartTitle}</h4>
                </div>
                <span class="dash-border w-100"></span>`;

            // Add each series point
            points.forEach((point: any) => {
              const value = point.y?.toLocaleString() || '0';
              const unit = component.unit || '';
              const valueWithUnit = unit ? `${value} ${unit}` : value;

              tooltipHtml += `
                <div class="d-flex align-items-center justify-content-between spacing-8 w-100">
                  <div class="d-flex align-items-center spacing-4 w-100 text-center">
                    <div style="background:${point?.color || point?.series?.color}" class="tooltipDot d-flex align-items-center h4-bold">&nbsp;</div>
                    <div class="h4-reg primary-50">${point.series.name}</div>
                  </div>
                  <div class="h4-bold primary-0">${valueWithUnit}</div>
                </div>`;
            });

            // Add date (no total for line series)
            tooltipHtml += `
                <span class="dash-border w-100"></span>
                <div class="d-flex align-items-center justify-content-between spacing-8 w-100">
                  <div class="h4-reg primary-50">${dateLabel}</div>
                </div>
              </div>
            </div>`;

            return tooltipHtml;
          }

          // Original logic for non-stacked charts
          const hasPrimaryAndCompare = points.length > 1;
          const primaryPoint = points[0];
          const comparePoint = points.length > 1 ? points[1] : null;

          let primaryValue = '';
          if (primaryPoint?.y !== undefined) {
            if (typeof primaryPoint.y === 'number' && primaryPoint.y >= 1000) {
              primaryValue = (primaryPoint.y / 1000).toFixed(1) + 'k';
            } else {
              primaryValue = primaryPoint.y.toLocaleString();
            }
          } else {
            primaryValue = '₹0.00';
          }

          const metricUnit = component.unit || primaryPoint?.series?.userOptions?.unit || '';
          let primaryValueWithUnit;
          if (metricUnit === '₹') {
            primaryValueWithUnit = `${metricUnit}${primaryValue}`;
          } else if (metricUnit) {
            primaryValueWithUnit = `${primaryValue} ${metricUnit}`;
          } else {
            primaryValueWithUnit = primaryValue;
          }

          let compareValueWithUnit = '';
          if (comparePoint?.y !== undefined) {
            let compareValue = '';
            if (typeof comparePoint.y === 'number' && comparePoint.y >= 1000) {
              compareValue = (comparePoint.y / 1000).toFixed(1) + 'k';
            } else {
              compareValue = comparePoint.y.toLocaleString();
            }
            if (metricUnit === '₹') {
              compareValueWithUnit = `${metricUnit}${compareValue}`;
            } else if (metricUnit) {
              compareValueWithUnit = `${compareValue} ${metricUnit}`;
            } else {
              compareValueWithUnit = compareValue;
            }
          }

          let percentageChange = 0;
          if (comparePoint?.y !== undefined && comparePoint.y !== 0) {
            percentageChange = ((primaryPoint.y - comparePoint.y) / Math.abs(comparePoint.y)) * 100;
          }
          percentageChange = isNaN(percentageChange) ? 0 : percentageChange;

          const changeColor = percentageChange >= 0 ? 'var(--success-100)' : 'var(--alert-100)';
          const changeSign = percentageChange >= 0 ? '+' : '';

          // Format date based on interval using the point's timestamp
          const formatDateLabel = (timestamp: number, interval: string | undefined): string => {
            const date = new Date(timestamp);
            const year = date.getFullYear();
            const monthShort = date.toLocaleString('en-US', { month: 'short' });
            const dayOfMonth = date.getDate();

            if (!interval) {
              // Default format if interval is unknown (Day format)
              return `${dayOfMonth} ${monthShort} ${year}`;
            }

            switch (interval) {
              case 'hour': {
                const currentHour = date.getHours();
                const nextHour = currentHour + 1;
                return `${dayOfMonth} ${monthShort} ${formatHour(currentHour)} - ${formatHour(nextHour)}`;
              }
              case 'week': {
                // Calculate start and end of the week (assuming Monday is start)
                const dayOfWeek = date.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
                const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Adjust for Sunday
                const diffToSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;

                const startOfWeek = new Date(date);
                startOfWeek.setDate(dayOfMonth + diffToMonday);
                const startDay = startOfWeek.getDate();
                const startMonth = startOfWeek.toLocaleString('en-US', { month: 'short' });

                const endOfWeek = new Date(date);
                endOfWeek.setDate(dayOfMonth + diffToSunday);
                const endDay = endOfWeek.getDate();
                const endMonth = endOfWeek.toLocaleString('en-US', { month: 'short' });
                const endYear = endOfWeek.getFullYear(); // Use end of week's year

                if (startMonth === endMonth) {
                  return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${endYear}`;
                } else {
                  // Handle week crossing over a month boundary
                  return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${endYear}`;
                }
              }
              case 'month': {
                return `${monthShort} ${year}`;
              }
              case 'year': {
                return `${year}`;
              }
              case 'day': // Explicitly handle 'day' or fall through default
              default:
                // Default to Day format
                return `${dayOfMonth} ${monthShort} ${year}`;
            }
          };

          let tooltipHtml = `
          <div class="custom-chart-tooltip" style="padding:12px;">
            <div class="d-flex flex-column spacing-8 align-items-start w-100">
              <div style="width: 100%;">
                <h4 class="h4-bold primary-0" style="margin: 0">${chartTitle}</h4>
              </div>
              <span class="dash-border w-100"></span>
              
              <!-- Primary data point -->
              <div class="d-flex align-items-center justify-content-between spacing-8 w-100">
                <div class="d-flex align-items-center spacing-4 w-100 text-center">
                  <div style="background:${primaryPoint?.color || primaryPoint?.series?.color}" class="tooltipDot d-flex align-items-center h4-bold">&nbsp;</div>
                  <div class="h4-reg primary-50">${formatDateLabel(primaryPoint?.x ?? Date.now(), context?.interval)}</div>
                </div>
                <div class="h4-bold primary-0">${primaryValueWithUnit}</div>
              </div>`;

          if (hasPrimaryAndCompare && comparePoint) {
            const compareDate = new Date(primaryPoint.x - (chartOptions.compareTimeOffset || 0));
            tooltipHtml += `
              <!-- Comparison data point -->
              <div class="d-flex align-items-center justify-content-between spacing-8 w-100">
                <div class="d-flex align-items-center spacing-4 w-100 text-center">
                  <div style="background:${comparePoint?.color || comparePoint?.series?.color}" class="tooltipDot d-flex align-items-center h4-bold">&nbsp;</div>
                  <div class="h4-reg primary-50">${formatDateLabel(compareDate.getTime(), context?.interval)}</div>
                </div>
                <div class="h4-bold primary-0">${compareValueWithUnit}</div>
              </div>
              
              <!-- Percentage change -->
              <div class="spacing-8" style="display:flex; justify-content:space-between; width:100%; align-items:center;">
                <div class="percentage-bar h4-bold" style="background:${changeColor}; color: #141415;">${changeSign}${percentageChange.toFixed(2)}%</div>
                <span class="h4-bold" style="color:'#141415';"> ${comparePeriodLabel}</span>
              </div>`;
          }

          tooltipHtml += `
            </div>
          </div>`;

          return tooltipHtml;
        } catch (error) {
          console.error('Tooltip error:', error);
          return 'Error displaying tooltip';
        }
      },
    },
    credits: {
      enabled: false,
    },

    series: [
      {
        marker: { enabled: false },
        color: 'var(--color-green)',
        lineWidth: 1.5,
        data: [],
      },
      {
        marker: { enabled: false },
        color: 'var(--primary-60)',
        dashStyle: 'Dot',
        lineWidth: 1.5,
        data: [],
      },
    ],

    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 500,
          },
          chartOptions: {
            legend: {
              layout: 'horizontal',
              align: 'center',
              verticalAlign: 'bottom',
            },
          },
        },
      ],
    },
  });

  @Output() onCompareToChange = new EventEmitter<ComparePeriod>();
  @Output() onSeriesClick = new EventEmitter<{
    seriesName: string;
    seriesIndex: number;
    pointValue: number;
    pointDate: Date;
    pointIndex: number;
  }>();

  protected readonly isNaN = isNaN;
  protected readonly actions: any = [];

  protected ready: boolean = true;
  protected options: any = undefined;
  protected comparePeriodLabel = signal<string>('');
  protected labels: string[] = [];
  protected change = signal<number | undefined>(undefined);
  protected value = signal<number | undefined>(undefined);
  protected formattedValue = signal<string | undefined | null>(undefined);

  // Add computed signal for compare period info
  protected comparePeriodInfo = computed(() => {
    return {
      label: getComparePeriodLabel(this.compare),
      period: this.compare,
    };
  });

  get changeType(): 'tag' | 'cancelled' | 'completed' {
    const changeVal = this.change();
    if (!changeVal) return 'tag';

    if (changeVal > 0) {
      return 'completed';
    }

    if (changeVal < 0) {
      return 'cancelled';
    }

    return 'tag';
  }

  get iconType(): 'caret_up' | 'caret_down' | 'active' {
    const changeVal = this.change();
    if (!changeVal) return 'active';

    if (changeVal > 0) {
      return 'caret_up';
    }

    if (changeVal < 0) {
      return 'caret_down';
    }

    return 'active';
  }

  constructor(
    private readonly formattingService: FormattingService,
    private readonly analyticsChartingService: AnalyticsGraphService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['context'] || changes['compare']) {
      this.comparePeriodLabel.set(getComparePeriodLabel(this.compare));
      const compareTimeOffset = this.calculateCompareTimeOffset();

      this.chartOptions.update(current => ({
        ...current,
        comparePeriodLabel: this.comparePeriodLabel(),
        comparePeriod: this.compare,
        context: this.context,
        compareTimeOffset,
      }));
    }

    const analytics = changes['analytics']?.currentValue as AnalyticsInfo<any>;
    if (!analytics || !this.context) return;

    const { change, value, series } = this.analyticsChartingService.getChartSettings(
      this.analytics!,
      this.metricsKey,
      this.title,
      this.formatter
    );

    // Update calculated value signals
    this.value.set(value);
    this.change.set(change);
    this.formattedValue.set(this.formatValue(value, this.formatter));

    let unit = '';
    if (typeof this.formatter === 'string') {
      switch (this.formatter) {
        case 'currency':
          unit = '₹';
          break;
        case 'number':
        case 'date':
          unit = '';
          break;
        default:
          if (this.formatter === 'percent') {
            unit = '%';
          } else if (this.formatter === 'time') {
            unit = 'days';
          }
          break;
      }
    }

    const seriesWithNames = Array.isArray(series)
      ? series.map(s => {
          if (!s.name && this.title) {
            return { ...s, name: this.title, unit };
          }
          return { ...s, unit };
        })
      : [];

    if (this.context) {
      const startDateObj = new Date(this.context.range.start);
      const endDateObj = new Date(this.context.range.end);

      // Determine chart type and stacking options based on chart mode
      const chartType = this.type === 'bar' ? 'column' : 'spline';

      this.chartOptions.set({
        ...this.chartOptions(),
        chart: {
          ...this.chartOptions().chart,
          type: chartType,
        },
        plotOptions: {
          ...this.chartOptions().plotOptions,
          column: {
            ...this.chartOptions().plotOptions.column,
            stacking: this.chartMode === 'stacked-bar' ? 'normal' : undefined,
            point:
              this.chartMode === 'stacked-bar'
                ? {
                    events: {
                      click: (event: any) => {
                        const point = event.point;
                        const series = point.series;
                        this.onSeriesClick.emit({
                          seriesName: series.name,
                          seriesIndex: series.index,
                          pointValue: point.y,
                          pointDate: new Date(point.x),
                          pointIndex: point.index,
                        });
                      },
                    },
                  }
                : undefined,
          },
        },
        legend: {
          ...this.chartOptions().legend,
          enabled:
            this.chartMode === 'stacked-bar' || this.chartMode === 'multiple-line' ? true : false,
          layout:
            this.chartMode === 'stacked-bar' || this.chartMode === 'multiple-line'
              ? 'horizontal'
              : 'vertical',
          align:
            this.chartMode === 'stacked-bar' || this.chartMode === 'multiple-line'
              ? 'center'
              : 'right',
          verticalAlign:
            this.chartMode === 'stacked-bar' || this.chartMode === 'multiple-line'
              ? 'bottom'
              : 'middle',
        },
        comparePeriodLabel: this.comparePeriodLabel(),
        currentRangeStart: startDateObj.getTime(),
        currentRangeEnd: endDateObj.getTime(),
        compareRangeStart: this.context.compare ? this.context.compare.start : null,
        compareTimeOffset: this.calculateCompareTimeOffset(),
        component: {
          name: this.title,
          unit: unit,
          formatter: this.formatter,
          isStacked: this.chartMode === 'stacked-bar',
          isMultipleLineSeries: this.chartMode === 'multiple-line',
        },
        xAxis: {
          ...this.chartOptions().xAxis,
          type: 'datetime',
          startOnTick: false,
          endOnTick: false,
        },
        series: this.processSeriesData(
          seriesWithNames,
          this.chartMode === 'stacked-bar' || this.chartMode === 'multiple-line'
        ),
      });
    }

    this.ready = true;
  }

  private calculateCompareTimeOffset(): number {
    if (!this.context?.compare || !this.context?.range?.start) {
      return 0;
    }

    // Calculate the offset between current range start and compare range start
    const currentStart = new Date(this.context.range.start).getTime();
    const compareStart = new Date(this.context.compare.start).getTime();
    return currentStart - compareStart;
  }

  onAction(item: any) {
    this.compare = item.value;
    this.onCompareToChange.emit(this.compare);
  }

  private formatValue(value: number, formatter: FormatterType | undefined) {
    return this.formattingService.formatValue(value ?? 0, formatter);
  }

  // Method to process series data based on display mode
  private processSeriesData(
    seriesSource: any[],
    isMultipleSeries: boolean = false
  ): Highcharts.SeriesOptionsType[] {
    if (isMultipleSeries && this.stackedSeries.length > 0) {
      return this.processMultipleSeriesData();
    }
    return this.processSingleSeriesData(seriesSource);
  }

  private processMultipleSeriesData(): Highcharts.SeriesOptionsType[] {
    return this.stackedSeries.map((seriesName, index) => {
      const color = this.getSeriesColor(index);
      const metricsKey = this.getMetricsKey(seriesName);
      const rawSeriesData = this.getRawSeriesData(metricsKey);
      const processedData = this.calculateCumulativeData(rawSeriesData);
      const seriesType = this.type === 'bar' ? 'column' : 'spline';
      const stacking = this.type === 'bar' ? 'normal' : undefined;

      return {
        name: this.getSeriesDisplayName(seriesName),
        type: seriesType,
        color: color,
        data: processedData,
        stacking: stacking,
        marker: { enabled: false },
        lineWidth: 1.5,
      };
    });
  }

  private processSingleSeriesData(seriesSource: any[]): Highcharts.SeriesOptionsType[] {
    const baseSeries = this.chartOptions().series;
    const currentMode = this.displayMode();

    return baseSeries.map((s: any, index: number) => {
      const newSeriesInfo =
        Array.isArray(seriesSource) && seriesSource[index] ? seriesSource[index] : null;
      const seriesData = newSeriesInfo ? newSeriesInfo.data : [];
      const sortedData = this.sortDataByTimestamp(seriesData);
      const processedData = this.calculateCumulativeData(sortedData, currentMode);
      const seriesName = newSeriesInfo ? newSeriesInfo.name || this.title : this.title;
      const seriesUnit = newSeriesInfo ? newSeriesInfo.unit || '' : '';

      return {
        ...s,
        name: seriesName,
        unit: seriesUnit,
        data: processedData,
      };
    });
  }

  private getSeriesColor(index: number): string {
    if (this.chartMode === 'multiple-line') {
      return (
        AnalyticsWidgetComponent.CHART_COLORS.LINE_COLORS[index] ||
        AnalyticsWidgetComponent.CHART_COLORS.DEFAULT_COLOR
      );
    } else if (this.chartMode === 'stacked-bar') {
      return (
        AnalyticsWidgetComponent.CHART_COLORS.BAR_COLORS[index] ||
        AnalyticsWidgetComponent.CHART_COLORS.DEFAULT_COLOR
      );
    }
    return AnalyticsWidgetComponent.CHART_COLORS.DEFAULT_COLOR;
  }

  private getMetricsKey(seriesName: string): string {
    if (this.type === 'bar') {
      switch (seriesName) {
        case 'Delivered':
          return AnalyticsWidgetComponent.METRICS_MAPPING.DELIVERED;
        case 'Failed':
          return AnalyticsWidgetComponent.METRICS_MAPPING.FAILED;
        default:
          return seriesName.toLowerCase();
      }
    }
    return seriesName;
  }

  private getRawSeriesData(metricsKey: string): any[] {
    return (
      this.analytics?.stats?.map((stat: any) => ({
        x: new Date(stat.date).getTime(),
        y: stat.metrics[metricsKey] || 0,
      })) || []
    );
  }

  private sortDataByTimestamp(data: any[]): any[] {
    return [...data].sort((a, b) => {
      const xA = typeof a.x === 'string' ? new Date(a.x).getTime() : a.x;
      const xB = typeof b.x === 'string' ? new Date(b.x).getTime() : b.x;
      return xA - xB;
    });
  }

  private calculateCumulativeData(data: any[], mode?: 'regular' | 'cumulative'): any[] {
    const currentMode = mode || this.displayMode();

    if (currentMode === 'cumulative') {
      let cumulativeValue = 0;
      return data.map((point: any) => {
        if (point && typeof point === 'object') {
          const x = typeof point.x === 'string' ? new Date(point.x).getTime() : point.x;
          const y = typeof point.y === 'number' ? point.y : 0;
          cumulativeValue += y;
          return { ...point, x: x, y: cumulativeValue };
        }
        return point;
      });
    } else {
      // Regular mode: just ensure timestamps are numbers
      return data.map((point: any) => {
        if (point && typeof point === 'object' && 'x' in point) {
          const x = typeof point.x === 'string' ? new Date(point.x).getTime() : point.x;
          return { ...point, x: x };
        }
        return point;
      });
    }
  }

  onViewModeChange(modeId: string) {
    this.displayMode.set(modeId as 'regular' | 'cumulative');
    // No need to manually trigger chart update here, the effect in constructor
    // or the computed signal dependency on displayMode should handle it.
    // We might need to re-run the chartOptions update if analytics data didn't change
    // but the mode did. Let's explicitly update chartOptions series.
    this.updateChartOptionsSeries();
  }

  // Helper method to get display names for series
  private getSeriesDisplayName(seriesName: string): string {
    return (
      AnalyticsWidgetComponent.SERIES_DISPLAY_NAMES[
        seriesName as keyof typeof AnalyticsWidgetComponent.SERIES_DISPLAY_NAMES
      ] || seriesName
    );
  }

  // Helper to specifically update the series part of chartOptions based on current analytics data
  private updateChartOptionsSeries() {
    const currentAnalytics = this.analytics;
    if (!currentAnalytics) return;

    const { series } = this.analyticsChartingService.getChartSettings(
      currentAnalytics,
      this.metricsKey,
      this.title,
      this.formatter
    );

    let unit = '';
    if (typeof this.formatter === 'string') {
      switch (this.formatter) {
        case 'currency':
          unit = '₹';
          break;
        case 'number':
          unit = '';
          break;
        case 'date':
          unit = '';
          break;
        default:
          if (this.formatter === 'percent') {
            unit = '%';
          } else if (this.formatter === 'time') {
            unit = 'days';
          }
          break;
      }
    }

    const seriesWithNames = Array.isArray(series) ? series.map(s => ({ ...s, unit })) : [];

    this.chartOptions.update(options => ({
      ...options,
      series: this.processSeriesData(
        seriesWithNames,
        this.chartMode === 'stacked-bar' || this.chartMode === 'multiple-line'
      ),
    }));

    // Force highcharts-angular update
    this.chartUpdateFlag++;
  }
}
